/* eslint-disable @typescript-eslint/camelcase */
import { CargoV2Mapper } from "@/data/persistences/mappers/CargoV2Mapper";
import { CargoV2RepositoryInterface } from "@/data/persistences/repositories/contracts/CargoV2RepositoryInterface";
import {
  CargoV2ListRequest,
  CargoV2DisputeDownloadRequest,
  CargoV2MinimumWeightSummaryApiRequest,
  CargoV2DownloadListRequest,
  CargoV2DownloadListFailedRequest,
  CargoV2DetailActivity,
  CargoV2ListClientParentApiRequest,
  CargoV2CalculateRequest,
  CargoV2SearchFlightRequest,
  AwbValidationApiRequest,
  CreateCargoV3ApiRequest,
  CalculateVolumeApiRequestV2,
  ScanApiRequest,
  RequestRtcList,
  RequestCheckEligibleRtcV2,
  DeleteRetryCargo,
  DownloadAWBRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import {
  CargoSummaryInProcessRequest,
  FailedUnreadRequest,
  AddCargoRequest
} from "@/data/payload/api/CargoApiRequest";
import { AddCargoRequestInteface } from "@/data/payload/contracts/CargoRequest";
import {
  CargoV2Data,
  CargoV2Entities,
  RtcListEntities,
  RtcData,
  CalculateData
} from "@/domain/entities/CargoV2";
import { CargoAddResponse, CargoCommodityList } from "@/domain/entities/Cargo";
import {
  CargoV2FailedUnread,
  CargoV2SummaryInProcessData,
  ListClientParentEntities,
  CargoV2SearchFlightResult,
  AwbDetailData
} from "@/domain/entities/CargoV2";
import { CargoEditResponse } from "@/domain/entities/Cargo";
import { CargoMapper } from "@/data/persistences/mappers/CargoMapper";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import ApiService from "@/app/infrastructures/services/ApiService";
import {
  CargoV2Endpoint,
  CARGO_V2_IN_PROGRESS_STATUS,
  CARGO_V2_CANCELLED_STATUS
} from "@/app/infrastructures/endpoints/CargoV2Endpoint";
import { Endpoints } from "@/app/infrastructures/misc/Endpoints";
import { downloadFile } from "@/app/infrastructures/misc/Utils";
import { EditCargoApiRequest } from "@/data/payload/api/CargoApiRequest";

export class CargoV2ApiRepository implements CargoV2RepositoryInterface {
  private service: ApiService;
  private mapper: CargoV2Mapper;
  private endpoints: CargoV2Endpoint;

  constructor(
    service: ApiService,
    mapper: CargoV2Mapper,
    endpoints: CargoV2Endpoint
  ) {
    this.service = service;
    this.mapper = mapper;
    this.endpoints = endpoints;
  }

  public async getCargoV2List(
    params: CargoV2ListRequest
  ): Promise<CargoV2Entities> {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getList(params),
      {}
    );
    return this.mapper.convertListFromApi(res);
  }

  public async getCargoV2ListInProgress(
    params: CargoV2ListRequest
  ): Promise<CargoV2Entities> {
    const inProgressParams = new CargoV2ListRequest({
      ...params,
      status: CARGO_V2_IN_PROGRESS_STATUS,
      isTotalData: true
    });
    const res = await this.service.invoke(
      "get",
      this.endpoints.getListRetryCargo(inProgressParams),
      {}
    );
    return this.mapper.convertListInProgressFromApi(res);
  }

  public async getCargoV2ListCancelled(
    params: CargoV2ListRequest
  ): Promise<CargoV2Entities> {
    const cancelledParams = new CargoV2ListRequest({
      ...params,
      status: CARGO_V2_CANCELLED_STATUS,
      isTotalData: true
    });
    const res = await this.service.invoke(
      "get",
      this.endpoints.getListRetryCargo(cancelledParams),
      {}
    );
    return this.mapper.convertListCancelledFromApi(res);
  }

  public async cancelBooking(id: string | number): Promise<ResponsePayload> {
    const res = await this.service.invoke(
      "POST",
      this.endpoints.cancel(id),
      undefined,
      undefined
    );
    return this.mapper.convertPayloadData(res);
  }

  public async deleteBooking(id: string | number): Promise<ResponsePayload> {
    const res = await this.service.invoke(
      "PATCH",
      this.endpoints.delete(id),
      undefined,
      new DeleteRetryCargo()
    );
    return this.mapper.convertPayloadData(res);
  }

  public async getCargoV2Detail(id: string | number): Promise<CargoV2Data> {
    const res = await this.service.invoke(
      "GET",
      this.endpoints.getDetailWithActivity(id)
    );
    return this.mapper.convertCargoDetailFromApi(res);
  }

  public async getCargoV2DetailForPrint(
    id: string | number,
    activity?: CargoV2DetailActivity
  ): Promise<CargoV2Data> {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getDetailWithActivity(id, activity),
      {}
    );
    return this.mapper.convertCargoDetailFromApi(res);
  }

  public async getSuccessSummary(params: CargoV2ListRequest) {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getSuccessSummary(params),
      {}
    );
    return this.mapper.convertCargoSuccessSummaryDataFromApi(res);
  }

  public async getDisputeSummary(params: CargoV2ListRequest) {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getDisputeSummary(params),
      {}
    );
    return this.mapper.convertCargoDisputeSummaryDataFromApi(res);
  }

  public async getMinimumVolumeSummary(
    params: CargoV2MinimumWeightSummaryApiRequest
  ) {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getMinimumVolumeSummary(params),
      {}
    );
    return this.mapper.convertCargoMinimumWeightSummaryFromApi(res);
  }

  public async downloadExcelCargoDisputeSummary(
    payload: CargoV2DisputeDownloadRequest
  ): Promise<void> {
    const body = {
      start_date: payload.startDate,
      end_date: payload.endDate,
      hub_ids: payload.hubIds,
      cargo_type: payload.cargoType
    };
    await downloadFile(
      `${Endpoints.baseUrl}${this.endpoints.downloadDisputeSummary()}`,
      payload.cargoType
        ? `CARGO_DISPUTE_${String(payload.cargoType).toUpperCase()}_${
            payload.startDate
          }_${payload.endDate}`
        : `CARGO_DISPUTE_${payload.startDate}_${payload.endDate}`,
      ".xlsx",
      true,
      60000,
      "POST",
      body,
      true
    );
  }

  public async downloadMinimumVolume(
    payload: CargoV2MinimumWeightSummaryApiRequest
  ): Promise<void> {
    const body = {
      start_date: payload.startDate,
      end_date: payload.endDate,
      hub_ids: payload.hubIds,
      cargo_type: payload.cargoType
    };
    await downloadFile(
      `${Endpoints.baseUrl}${this.endpoints.downloadMinimumVolume()}`,
      payload.cargoType
        ? `CARGO_MINIMUM_WEIGHT_${String(payload.cargoType).toUpperCase()}_${
            payload.startDate
          }_${payload.endDate}`
        : `CARGO_MINIMUM_WEIGHT_${payload.startDate}_${payload.endDate}`,
      ".xlsx",
      true,
      60000,
      "POST",
      body,
      true
    );
  }

  public async downloadListCargoSuccess(
    payload: CargoV2DownloadListRequest
  ): Promise<ResponsePayload> {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.downloadListCargoSuccess(),
      {},
      payload
    );
    return this.mapper.convertPayloadData(resp);
  }

  public async downloadAwbReceipt(
    id: string | number
  ): Promise<ResponsePayload> {
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.downloadReceipt(id)
    );
    return this.mapper.convertPayloadData(resp);
  }

  public async downloadAwbReceiptSync(payload: DownloadAWBRequest) {
    return downloadFile(
      `${Endpoints.baseUrl}${this.endpoints.downloadReceiptSync(payload.id)}`,
      `Print AWB - ${payload.awbNumber}`,
      ".pdf",
      true
    );
  }

  public async downloadListCargoFailed(
    params: CargoV2ListRequest
  ): Promise<ResponsePayload> {
    const payload = new CargoV2DownloadListFailedRequest({
      search: params.search,
      cargoType: params.cargoType,
      startDate: params.startDate,
      endDate: params.endDate,
      destinationCity: params.destinationCity,
      destinationAirport: params.destinationAirport,
      hubIds: params.hubIds
    });
    const resp = await this.service.invoke(
      "POST",
      this.endpoints.downloadFailed(),
      {},
      payload
    );
    return this.mapper.convertPayloadData(resp);
  }

  public async getSummaryInProcess(
    params: CargoSummaryInProcessRequest
  ): Promise<CargoV2SummaryInProcessData> {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getSummaryInProcess(params),
      {}
    );
    return this.mapper.convertCargoSummaryInProcess(res);
  }

  public async getCargoFailedUnread(
    params: FailedUnreadRequest
  ): Promise<CargoV2FailedUnread> {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getFailedUnread(params),
      {}
    );
    return this.mapper.convertCargoFailedUnreadFromApi(res);
  }

  public async getLastIdRetryCargo(
    params: CargoV2ListRequest
  ): Promise<number> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getLastIdRetryCargo(params),
      {}
    );
    return this.mapper.convertLastIdCargoAndRetryCargo(resp);
  }

  public async getLastIdCargo(params: CargoV2ListRequest): Promise<number> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getLastIdCargo(params),
      {}
    );
    return this.mapper.convertLastIdCargoAndRetryCargo(resp);
  }

  public async getDownloadReport(id: number, type: string, filename = ""): Promise<any> {
    return downloadFile(
      `${Endpoints.baseUrl}${this.endpoints.downloadManifest(
        `report/download/${type}/${id}`
      )}`,
      type === "epl" ? `EPL_Manifest_${id}` : `${filename}`,
      ".xlsx",
      true
    );
  }

  public async getRTCListClientParent(
    params: CargoV2ListClientParentApiRequest
  ): Promise<ListClientParentEntities> {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getRTCListClientParent(params),
      {}
    );
    return this.mapper.convertAirportDataFromApi(res);
  }

  public async searchFlight(
    payload: CargoV2SearchFlightRequest
  ): Promise<CargoV2SearchFlightResult> {
    const res = await this.service.invoke(
      "post",
      this.endpoints.searchFlight(),
      {},
      payload,
      undefined,
      payload.signal
    );
    return this.mapper.convertSearchFlightFromApi(res);
  }
  public async getReadyToCargoList(
    params: RequestRtcList
  ): Promise<RtcListEntities> {
    const res = await this.service.invoke(
      "post",
      this.endpoints.getReadyToCargoList(),
      {},
      params
    );
    return this.mapper.convertRtcListFromApi(res);
  }

  public async checkEligible(payload: RequestCheckEligibleRtcV2) {
    const res = await this.service.invoke(
      "POST",
      this.endpoints.checkEligibleRtc(),
      undefined,
      payload
    );
    return this.mapper.convertCheckEligibleRtcFromApi(res);
  }

  public async getDetailReadyToCargo(id: number): Promise<RtcData> {
    const res = await this.service.invoke(
      "get",
      this.endpoints.getDetailReadyToCargo(id),
      {}
    );
    return this.mapper.convertRtcDetailFromApi(res);
  }

  public async createCargoV3(
    payload: AddCargoRequestInteface
  ): Promise<CargoAddResponse> {
    const resp = await this.service.invoke(
      "post",
      new Endpoints().createCargoV3(),
      undefined,
      payload as AddCargoRequest
    );
    return this.mapper.convertCargoAddFromApi(resp);
  }

  public async createCargo(payload: CreateCargoV3ApiRequest) {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.create(),
      undefined,
      payload
    );
    return this.mapper.convertCreateCargoFromApi(resp);
  }

  public async editCargoV3(
    payload: EditCargoApiRequest
  ): Promise<CargoEditResponse> {
    const resp = await this.service.invoke(
      "put",
      new Endpoints().createCargoV3(),
      undefined,
      payload
    );
    return new CargoMapper().convertCargoEditFromApi(resp);
  }

  public async getCargoCommodityList(
    page: number,
    limit: number,
    search: string
  ): Promise<CargoCommodityList> {
    const resp = await this.service.invoke(
      "get",
      new Endpoints().getCargoCommodityList(page, limit, search),
      {}
    );
    return this.mapper.convertCargoCommodityFromApi(resp);
  }

  public async calculate(
    payload: CargoV2CalculateRequest
  ): Promise<CalculateData> {
    const resp = await this.service.invoke(
      "POST",
      new Endpoints().calculateVolume(),
      undefined,
      payload
    );

    return this.mapper.convertCalculateFromApi(resp);
  }

  public async getCargoProductList(status: string) {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getCargoProductList(status),
      {}
    );
    return this.mapper.convertCargoProductDataFromApi(resp);
  }

  public async getCargoHistory(id: string) {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getCargoHistory(id),
      {}
    );
    return this.mapper.convertCargoHistoryFromApi(resp);
  }

  public async getAwbValidation(
    payload: AwbValidationApiRequest
  ): Promise<AwbDetailData> {
    const res = await this.service.invoke(
      "post",
      this.endpoints.getAwbValidation(),
      {},
      payload
    );
    return this.mapper.convertAwbDataFromApi(res);
  }

  async calculateVolume(payloadData: CalculateVolumeApiRequestV2) {
    const { signal, ...payload } = payloadData;

    const resp = await this.service.invoke(
      "POST",
      this.endpoints.calculateVolume(),
      {},
      new CalculateVolumeApiRequestV2(payload),
      undefined,
      signal
    );

    return this.mapper.convertCalculateVolumeFromApi(resp);
  }

  async getCargoTransportType(origin: string, destination: string) {
    const resp = await this.service.invoke(
      "GET",
      new Endpoints().getSearchCargoOptions(origin, destination)
    );

    return this.mapper.convertCargoTransportTypeFromApi(resp);
  }

  public async getDetailRetryCargo(rcNo: string): Promise<CargoV2Data> {
    const resp = await this.service.invoke(
      "get",
      this.endpoints.getDetailRetryCargo(rcNo),
      {}
    );
    return this.mapper.convertRetryCargoDetailFromApi(resp);
  }

  public async scan(payload: ScanApiRequest) {
    const resp = await this.service.invoke(
      "post",
      this.endpoints.scan(),
      undefined,
      payload
    );
    return this.mapper.convertScanDataFromApi(resp);
  }
}
